/**
 * The recorded room (Front row), the page a registrant lands on for a talk that plays any time. Built to the
 * confirmed prototype (g-front, "The recorded room"): the brand and the talk across the top with "Your page" and
 * the chat; the player; under it the chapter, the title, the host's words now, the chapters as frames, what the end
 * leaves them with; the chat in a drawer on a laptop and a sheet on a phone, with a dock at the bottom of a phone.
 *
 * Everything that counts keeps today's rules: the watch signal (`useWatchProgress`), the moments' scheduler
 * (`useArrivals`), the workspace's measurement tags (`useWatchMeasure`), the offer's press and its video's tags.
 */
import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { guestLook } from "../../../shared/guest-look";
import { OFFER_FOLD_MS, offerArrived, offerClosed } from "../../../shared/offer";
import type {
  HandoutConfig,
  WatchInteraction,
  WatchPageResponse,
  WatchSession,
  WatchTalkResponse,
} from "../../../shared/types";
import { api, assetUrl } from "../../lib/api";
import { applyBrandIcon } from "../brandIcon";
import { useWatchMeasure } from "../measureWatch";
import { type Arrivals, firstName, properName } from "../TalkPlayer";
import { useArrivals } from "../useArrivals";
import { useWatchProgress } from "../watchProgress";
import { useWatchThread } from "../WatchPageView";
import { Brand, Made, useGuestGround } from "./chrome";
import { GIcon } from "./GIcon";
import { RoomChat } from "./RoomChat";
import { OfferDock, OfferEnd, type OfferProps } from "./RoomOffer";
import { ChapterFrame, ROOMY_PX, RoomPlayer } from "./RoomPlayer";
import { chapterAt, lineAt, mmss, waitingQuestion } from "./talk";
import { useRoomVideo } from "./useRoomVideo";
import "../../styles/guest.css";

/** A playhead step shorter than this is the talk playing across the offer, not a jump past it. */
const OFFER_CROSS_SEC = 3;
/** The controls fade after this long while the talk plays. */
export const IDLE_MS = 2800;
const WIDE = "(min-width:1024px)";


export function RoomPage({ token, data, session }: { token: string; data: WatchPageResponse; session: WatchSession }): ReactNode {
  const { brand } = data;
  const host = firstName(session.host_name);
  const viewerFirst = properName(data.registrant_name || "there");
  const look = useMemo(() => guestLook(brand), [brand]);
  useGuestGround(look);

  // The talk as this viewer leaves it: an answer lands on its moment here.
  const [talk, setTalk] = useState(session);
  const setMine = useCallback((id: string, value: WatchInteraction["my_response"]) => {
    setTalk((s) => ({ ...s, interactions: s.interactions.map((i) => (i.id === id ? { ...i, my_response: value } : i)) }));
  }, []);
  const { arrivals, answer, again: askAgain } = useArrivals({ token, session: talk, active: true, setMine });
  const measure = useWatchMeasure(data.measurement, talk, data.meta_sent_earlier);
  const measured = useMemo<Arrivals>(
    () => ({ ...arrivals, onTime: (tick) => { arrivals.onTime(tick); measure.tick(tick); } }),
    [arrivals, measure],
  );
  const report = useWatchProgress(token, session.webinar_id, () => {});
  const v = useRoomVideo({ session: talk, arrivals: measured, onProgress: report });

  // The frames and the words, asked for once the room opens.
  const [extra, setExtra] = useState<WatchTalkResponse>({ frames: null, captions: [] });
  useEffect(() => {
    let alive = true;
    api.watch
      .talk(token, session.webinar_id)
      .then((res) => {
        if (alive) setExtra(res);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [token, session.webinar_id]);
  const { frames, captions } = extra;

  useEffect(() => {
    document.title = session.title;
  }, [session.title]);
  useEffect(() => {
    applyBrandIcon(brand);
  }, [brand]);

  // ── what the viewer sees: the words on screen, the menus, the controls awake ──
  const [cc, setCc] = useState(true);
  const [menu, setMenu] = useState<"spd" | "chs" | null>(null);
  const [idle, setIdle] = useState(false);
  const idleTimer = useRef(0);
  const poke = useCallback(() => {
    setIdle(false);
    window.clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(() => setIdle(true), IDLE_MS);
  }, []);
  useEffect(() => () => window.clearTimeout(idleTimer.current), []);
  useEffect(() => {
    if (menu) setIdle(false);
  }, [menu]);
  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest(".rm-menu,.rm-speed,[aria-label='Chapters']")) setMenu(null);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [menu]);

  // ── before they press play: the big play, or welcome back ──
  const [virgin, setVirgin] = useState(true);
  useEffect(() => {
    if (v.playing) setVirgin(false);
  }, [v.playing]);
  const resumeAsk = virgin && session.watched_sec > 5 && session.watched_pct < 95;

  // ── the picture: big enough for a moment inside it, or not; full screen ──
  const picRef = useRef<HTMLDivElement>(null);
  const [roomy, setRoomy] = useState(true);
  const [fs, setFs] = useState(false);
  useEffect(() => {
    const pic = picRef.current;
    const frame = pic?.querySelector(".rm-frame");
    if (!frame || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setRoomy(fs || (frame as HTMLElement).clientHeight >= ROOMY_PX));
    ro.observe(frame);
    return () => ro.disconnect();
  }, [fs]);
  const toggleFs = useCallback(() => {
    const pic = picRef.current;
    if (fs) {
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
      setFs(false);
      return;
    }
    setFs(true);
    void pic?.requestFullscreen?.().catch(() => {
      /* stays as a full-window player */
    });
  }, [fs]);
  useEffect(() => {
    const left = () => {
      if (!document.fullscreenElement) setFs(false);
    };
    document.addEventListener("fullscreenchange", left);
    return () => document.removeEventListener("fullscreenchange", left);
  }, []);
  useEffect(() => {
    document.body.style.overflow = fs ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [fs]);

  // ── the offer: lands at its second, open while the talk plays, folds itself after a while ──
  const theOffer = talk.offer;
  const [offer, setOffer] = useState(() => ({ seen: !!theOffer && offerArrived(theOffer, session.watched_sec), open: false }));
  const foldTimer = useRef(0);
  const openOffer = useCallback((open: boolean) => {
    window.clearTimeout(foldTimer.current);
    setOffer((o) => ({ ...o, open }));
  }, []);
  // Why the last second is kept: only the talk playing across the offer opens it; a jump past it lands it as a tab.
  const lastCur = useRef(v.cur);
  useEffect(() => {
    const from = lastCur.current;
    lastCur.current = v.cur;
    if (!theOffer || offer.seen || !offerArrived(theOffer, v.cur)) return;
    const open = v.playing && !arrivals.up && v.cur - from >= 0 && v.cur - from < OFFER_CROSS_SEC;
    setOffer({ seen: true, open });
    if (open) foldTimer.current = window.setTimeout(() => setOffer((o) => ({ ...o, open: false })), OFFER_FOLD_MS.room);
  }, [theOffer, offer.seen, v.cur, v.playing, arrivals.up]);
  useEffect(() => {
    if (arrivals.up) setOffer((o) => (o.open ? { ...o, open: false } : o));
  }, [arrivals.up]);
  useEffect(() => {
    if (v.ended && theOffer) setOffer({ seen: true, open: false });
  }, [v.ended, theOffer]);
  useEffect(() => () => window.clearTimeout(foldTimer.current), []);
  // An offer set to show for a while closes once that while has passed in the talk.
  const offerGone = !!theOffer && offerClosed(theOffer, v.cur);

  // ── the chat: a drawer on a laptop, a sheet on a phone; the dot until they look ──
  // Why a line comes back after a send: the × leaves out the time of this one message only.
  const [momentOn, setMomentOn] = useState(true);
  const playSec = Math.min(Math.floor(v.cur), Math.floor(v.total));
  const thread = useWatchThread(token, session, undefined, () => (momentOn ? { at_sec: playSec } : {}));
  const mineCount = thread.messages.filter((m) => m.sender !== "host").length;
  useEffect(() => {
    setMomentOn(true);
  }, [mineCount]);
  const [chatOn, setChatOn] = useState(false);
  const hostCount = thread.messages.filter((m) => m.sender === "host").length;
  // Why from the last visit: what the host wrote since they were last here is what they have not read yet.
  const [readCount, setReadCount] = useState(
    () => thread.messages.filter((m) => m.sender === "host" && m.created_at <= session.last_visit_at).length,
  );
  useEffect(() => {
    if (chatOn) setReadCount(hostCount);
  }, [chatOn, hostCount]);
  const unread = hostCount > readCount;
  const [wide, setWide] = useState(() => typeof matchMedia !== "undefined" && matchMedia(WIDE).matches);
  useEffect(() => {
    if (typeof matchMedia === "undefined") return;
    const mq = matchMedia(WIDE);
    const on = () => setWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const openChat = useCallback(() => {
    if (fs) toggleFs();
    setChatOn(true);
    window.setTimeout(() => {
      if (matchMedia(WIDE).matches) thread.field.current?.focus({ preventScroll: true });
    }, 400);
  }, [fs, toggleFs, thread.field]);

  // ── taps and floats ──
  const [tap, setTap] = useState<{ side: "l" | "r"; n: number } | null>(null);
  const onTap = useCallback(
    (side: "l" | "r") => {
      v.seek(v.cur + (side === "l" ? -10 : 10));
      setTap((t) => ({ side, n: (t?.n ?? 0) + 1 }));
      poke();
    },
    [v, poke],
  );
  const [floats, setFloats] = useState<Array<{ id: number; emoji: string; left: number; dx: number }>>([]);
  const floatId = useRef(0);
  const onFloat = useCallback((emoji: string) => {
    const id = ++floatId.current;
    setFloats((f) => [...f, { id, emoji, left: 60 + Math.random() * 25, dx: Math.random() * 60 - 30 }]);
    window.setTimeout(() => setFloats((f) => f.filter((x) => x.id !== id)), 3300);
  }, []);

  // ── the keyboard ──
  const keys = useRef({ v, menu, moment: arrivals.up, fs, toggleFs, onTap, dismiss: arrivals.dismiss, setCc });
  keys.current = { v, menu, moment: arrivals.up, fs, toggleFs, onTap, dismiss: arrivals.dismiss, setCc };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = keys.current;
      if (e.key === "Escape") {
        if (k.menu) setMenu(null);
        else if (k.moment) k.dismiss(k.moment.id);
        else if (k.fs && !document.fullscreenElement) k.toggleFs();
        else setChatOn(false);
        return;
      }
      const target = e.target as HTMLElement;
      if (target.closest("input,textarea,select")) return;
      if ((e.key === " " || e.key === "Enter") && target.closest("button,a")) return;
      if (e.key === " " || e.key === "k") {
        e.preventDefault();
        k.v.toggle();
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        k.v.seek(k.v.cur + 5);
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        k.v.seek(k.v.cur - 5);
      }
      if (e.key === "l") k.onTap("r");
      if (e.key === "j") k.onTap("l");
      if (e.key === "f") k.toggleFs();
      if (e.key === "m") k.v.toggleMute();
      if (e.key === "c") k.setCc((on) => !on);
      poke();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [poke]);

  // ── the glow behind the picture, from the picture itself ──
  const amb = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = amb.current;
    const ctx = canvas?.getContext?.("2d");
    if (!canvas || !ctx) return;
    let poster: HTMLImageElement | null = null;
    if (session.poster_url) {
      poster = new Image();
      poster.src = session.poster_url;
      poster.onload = () => {
        if (poster) ctx.drawImage(poster, 0, 0, canvas.width, canvas.height);
      };
    }
    const every = window.setInterval(() => {
      const video = v.videoRef.current;
      if (!video || video.readyState < 2 || video.paused) return;
      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      } catch {
        /* a frame the browser will not draw leaves the last glow */
      }
    }, 600);
    return () => window.clearInterval(every);
  }, [session.poster_url, v.videoRef]);

  // ── the offer and what the player needs to draw it ──
  const convert = useCallback(() => {
    if (talk.offer) measure.press(talk.offer.kind);
    api.watch.signup(token, { webinar_id: session.webinar_id }).catch(() => {});
  }, [talk.offer, measure, token, session.webinar_id]);
  const offerVideo = useCallback(
    (state: "play" | "end") => {
      if (state === "play") v.pause();
      api.watch.offerVideo(token, { webinar_id: session.webinar_id, state }).catch(() => {});
    },
    [v, token, session.webinar_id],
  );
  const offerProps: OfferProps | null = talk.offer
    ? {
        offer: talk.offer,
        host,
        hostAvatar: session.host_avatar_url,
        said: typeof talk.offer.appears_at === "number" ? (lineAt(captions, talk.offer.appears_at)?.text ?? null) : null,
        viewerEmail: data.registrant_email,
        onConvert: convert,
        onChat: openChat,
        onNote: offerVideo,
      }
    : null;

  // ── the end: what the talk leaves them with ──
  const pageHref = `/watch/${encodeURIComponent(token)}`;
  const lastHandout = [...talk.interactions].reverse().find((i) => i.type === "handout");
  const lastWords = captions.length > 0 ? captions[captions.length - 1].text : null;
  const handoutRow = lastHandout ? (
    <HandoutRow moment={lastHandout} onTake={() => answer(lastHandout.id, { kind: "handout", downloaded: true })} />
  ) : null;
  const waiting = waitingQuestion(thread.messages);
  const endRight = (
    <div className="end-r">
      {offerProps && !offerGone ? <OfferEnd {...offerProps} /> : null}
      {handoutRow}
      <div className="end-keep">
        <GIcon name="home" size={20} />
        {waiting?.at_sec !== undefined ? (
          <span>
            <b>
              Your question at {mmss(waiting.at_sec)} is with {host}.
            </b>
            The answer comes to your page and your email.
          </span>
        ) : (
          <span>Your page keeps everything: the handout, your answers and this chat.</span>
        )}
        <a href={pageHref}>{waiting ? "Open your page" : "Open it"}</a>
      </div>
    </div>
  );
  const note = lastWords ? (
    <div className="end-note">
      {session.host_avatar_url ? <img className="av" src={session.host_avatar_url} alt="" /> : null}
      <div>
        <small><bdi>{host}</bdi>, at the end</small>
        <p>“{lastWords}”</p>
      </div>
    </div>
  ) : null;
  const again = () => {
    v.restart();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const endFull = (
    <div className="end-c">
      <div>
        <p className="eyebrow">You watched it to the end</p>
        <h2 className="h-1" style={{ marginTop: 12 }}>
          That’s the talk, {viewerFirst}.
        </h2>
        {note}
        <div className="end-acts">
          <button className="btn btn-g" type="button" onClick={again}>
            <GIcon name="replay" size={18} /> Watch again
          </button>
          <button className="btn btn-q" type="button" onClick={openChat}>
            Write to {host}
          </button>
        </div>
      </div>
      {endRight}
    </div>
  );

  const ci = chapterAt(talk.chapters, v.cur);
  const chapter = talk.chapters[ci];
  // Before the first sentence the box says so in the page's own words, never in quotes as if the host had said it.
  const saidNow = lineAt(captions, v.cur);
  const over = v.ended ? (
    <div className="rm-endov">
      <div className="end-mini">
        <h2>That’s the talk.</h2>
        <div className="end-acts" style={{ justifyContent: "center", marginTop: 14 }}>
          <button className="btn btn-l btn-m" type="button" onClick={again}>
            <GIcon name="replay" size={18} /> Watch again
          </button>
        </div>
      </div>
      <div className="end-full">{endFull}</div>
    </div>
  ) : resumeAsk ? (
    <div className="rm-resume">
      <p className="rs-p">Welcome back, {viewerFirst}.</p>
      <h2>Pick up where you stopped?</h2>
      {chapter ? <p dir="auto">{chapter.title}</p> : null}
      <div className="row">
        <button className="btn btn-p" type="button" onClick={v.play}>
          <GIcon name="play" size={16} /> Continue from {mmss(session.watched_sec)}
        </button>
        <button className="btn btn-g" type="button" onClick={v.restart}>
          Start from the beginning
        </button>
      </div>
    </div>
  ) : virgin && !v.playing ? (
    <div className="rm-big">
      <button className="playc" type="button" aria-label="Play the talk" onClick={v.play}>
        <GIcon name="play" size={32} />
      </button>
      {session.watched_sec > 0 ? <p>Continue from {mmss(session.watched_sec)}</p> : null}
    </div>
  ) : null;

  const rmClass = ["rm", resumeAsk ? "resuming" : "", virgin && !v.playing && !v.ended ? "virgin" : "", v.ended ? "ended" : "", chatOn ? "chat-on" : "", offer.seen && !offerGone ? "has-offer" : ""]
    .filter(Boolean)
    .join(" ");
  const mins = Math.round(v.total / 60);

  return (
    <div className="gst" style={look as CSSProperties}>
      <div
        className={rmClass}
        data-play={v.playing ? "1" : "0"}
        data-idle={idle && v.playing && !menu ? "1" : "0"}
        onPointerMove={poke}
        onPointerDown={poke}
      >
        <div className="amb-wrap">
          <canvas className="amb" ref={amb} width={96} height={54} />
        </div>
        <header className="rm-top">
          <Brand brand={brand} />
          <div className="rm-tt">
            <b dir="auto">{session.title}</b>
            <span>{chapter ? <>Chapter {ci + 1} · <bdi>{chapter.title}</bdi></> : ""}</span>
          </div>
          <div className="rm-tr">
            <a className="top-link" href={pageHref}>
              <GIcon name="home" size={16} /> Your page
            </a>
            <button className="rm-chatbtn" type="button" onClick={openChat}>
              {session.host_avatar_url ? <img className="av" src={session.host_avatar_url} alt="" /> : null}
              <span>Chat with <bdi>{host}</bdi></span>
              <i className="dot" hidden={!unread} />
            </button>
          </div>
        </header>
        <div className="rm-body">
          <div className="rm-stage">
            <RoomPlayer
              session={talk}
              v={v}
              host={host}
              hostAvatar={session.host_avatar_url}
              frames={frames}
              captions={captions}
              cc={cc}
              onCc={() => setCc((on) => !on)}
              arrivals={measured}
              onAnswer={answer}
              onAgain={askAgain}
              over={over}
              offer={talk.offer && !offerGone ? offer : null}
              offerProps={offerProps}
              onOffer={openOffer}
              fs={fs}
              onFs={toggleFs}
              picRef={picRef}
              menu={menu}
              setMenu={setMenu}
              poke={poke}
              tap={tap}
              onTap={onTap}
              floats={floats}
              onFloat={onFloat}
              roomy={roomy}
            />
          </div>
        </div>
        <aside className="rm-side" aria-label={`Chat with ${host}`}>
          <span className="grab" />
          <div className="side-chat">
            <RoomChat
              session={session}
              thread={thread}
              host={host}
              onClose={() => setChatOn(false)}
              moment={{ sec: playSec, on: momentOn, setOn: setMomentOn }}
            />
          </div>
        </aside>
        <section className="rm-below">
          <p className="rm-meta">
            {chapter ? (
              <>
                Chapter {ci + 1} of {talk.chapters.length} <span>· <bdi>{chapter.title}</bdi></span>
              </>
            ) : null}
          </p>
          <h1 dir="auto">{session.title}</h1>
          {captions.length > 0 ? (
            <div className={cc ? "rm-line" : "rm-line off"}>
              <small>
                {session.host_avatar_url ? <img className="av" src={session.host_avatar_url} alt="" /> : null}
                {host}, now
              </small>
              {saidNow ? <p>“{saidNow.text}”</p> : <p className="wait"><bdi>{host}</bdi> is getting started.</p>}
            </div>
          ) : null}
          {talk.chapters.length > 0 ? (
            <div className="rm-chs">
              <h2>
                Chapters <span>{mins} min</span>
              </h2>
              <div className="film">
                {talk.chapters.map((c, i) => (
                  <ChapterFrame
                    key={`${c.t}-${c.title}`}
                    t={c.t}
                    title={c.title}
                    on={i === ci}
                    frames={frames}
                    poster={session.poster_url}
                    onJump={(t) => {
                      v.seek(t);
                      v.play();
                    }}
                  />
                ))}
              </div>
            </div>
          ) : null}
          <div className="rm-endbelow">
            {v.ended ? (
              <>
                <p className="eyebrow">You watched it to the end</p>
                <h2 style={{ marginTop: 8 }}>That’s the talk, {viewerFirst}.</h2>
                {note}
                {endRight}
              </>
            ) : null}
          </div>
          <div className="rm-foot">
            <span>{brand.workspace_name}</span>
            <Made />
          </div>
        </section>
        <nav className="rm-dock">
          <button className="dk-chat" type="button" onClick={openChat}>
            {session.host_avatar_url ? <img className="av" src={session.host_avatar_url} alt="" /> : null}
            <span className="dk-l">Chat with <bdi>{host}</bdi></span>
            <span className="dk-s">Chat</span>
            <i className="dot" hidden={!unread} />
          </button>
          {offer.seen && !offerGone && offerProps ? <OfferDock {...offerProps} /> : null}
        </nav>
        <div className={chatOn && !wide ? "scrim on" : "scrim"} onClick={() => setChatOn(false)} />
      </div>
    </div>
  );
}

/** The talk's handout at the end: the file and its download, saved once taken. */
function HandoutRow({ moment, onTake }: { moment: WatchInteraction; onTake: () => void }): ReactNode {
  const cfg = moment.config as HandoutConfig;
  const saved = moment.my_response?.kind === "handout";
  const meta = ["PDF", (cfg.size ?? "").trim(), `from ${mmss(moment.appears_at)}`].filter(Boolean).join(" · ");
  const inner = (
    <>
      <GIcon name={saved ? "check" : "down"} size={16} /> {saved ? "Saved" : "Download"}
    </>
  );
  return (
    <div className="file">
      <span className="file-ic">PDF</span>
      <div>
        <b>{cfg.title || cfg.file}</b>
        <span>{meta}</span>
      </div>
      {cfg.key ? (
        <a className={saved ? "btn done" : "btn btn-g"} href={assetUrl(cfg.key)} download={cfg.file} target="_blank" rel="noreferrer" onClick={onTake}>
          {inner}
        </a>
      ) : (
        <button className={saved ? "btn done" : "btn btn-g"} type="button" disabled={saved} onClick={onTake}>
          {inner}
        </button>
      )}
    </div>
  );
}
